import * as React from 'react';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { FormRangeWithInputField } from '@hvcgroep/design-system';
import { RegistrationType } from '../../types';
import { gjtom3 } from '../../helpers/gasCalculation';
import { RegistrationDeskEventHandler } from '../../state-machine';
import { APP_ID, WIDGET_OPTIONS } from '../../../../shared/config/constants';
import { USAGE_SLIDER_TIMEOUT } from '../../config/constants';
import gtm from '../../../../shared/helpers/gtm';
import { heatApi } from '../../store/slices/HeatApi';

type Props = {
  registrationType: RegistrationType,
  onUpdate: RegistrationDeskEventHandler<'proceed.updateUsage'>,
  sjvM3: string,
  sjvGJ: string,
  ean: string,
};

const AdjustUsageSlider: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const {
    registrationType,
    sjvGJ,
    sjvM3,
    ean,
    onUpdate,
  } = props;
  const [touched, setTouched] = React.useState(false);
  const [trigger] = heatApi.endpoints.getOffer.useLazyQuerySubscription();

  // Re-fetch offer whenever the SJV changes.
  React.useEffect(() => {
    if (touched) {
      trigger({
        ean,
        sjvGJ,
      });
    }
  }, [sjvGJ]);

  const [throttler, setThrottler] = React.useState<ReturnType<typeof setTimeout> | null>(null);

  return (
    <div>
      <Formik
        initialValues={{}}
        onSubmit={() => {}}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {() => {
          return (
            <Form className="AdjustUsageForm">
              <FormRangeWithInputField
                className="no-spacing"
                unit={registrationType === 'moving' ? 'GJ' : 'm\u00B3'}
                defaultValue={registrationType === 'moving' ? Number(sjvGJ) : Number(sjvM3)}
                min={registrationType === 'moving' ? WIDGET_OPTIONS?.registrationDeskOptions?.minSjvGj : WIDGET_OPTIONS?.registrationDeskOptions?.minSjvM3}
                max={registrationType === 'moving' ? WIDGET_OPTIONS?.registrationDeskOptions?.maxSjvGj : WIDGET_OPTIONS?.registrationDeskOptions?.maxSjvM3}
                name="gas_usage"
                label={registrationType === 'moving' ? t('registration_desk.adjust_usage_slider.label.moving') : t('registration_desk.adjust_usage_slider.label.renovation')}
                placeholder="0"
                unitAfter
                required
                horizontal={false}
                tooltip={registrationType === 'renovation' ? t('registration_desk.adjust_usage_slider.tooltip') : undefined}
                allowDecimals
                description={t('registration_desk.adjust_usage_slider.description', {
                  min: registrationType === 'moving' ? `${String(WIDGET_OPTIONS?.registrationDeskOptions?.minSjvGj)} GJ` : `${String(WIDGET_OPTIONS?.registrationDeskOptions?.minSjvM3)} m\u00B3`,
                  max: registrationType === 'moving' ? `${String(WIDGET_OPTIONS?.registrationDeskOptions?.maxSjvGj)} GJ` : `${String(WIDGET_OPTIONS?.registrationDeskOptions?.maxSjvM3)} m\u00B3`,
                })}
                onTooltipOpen={() => {
                  gtm.trackUserEvent(String(APP_ID), 'Open Tooltip', t('registration_desk.buttons.adjust_usage'));
                }}
                onTooltipClose={() => {
                  gtm.trackUserEvent(String(APP_ID), 'Close Tooltip', t('registration_desk.buttons.adjust_usage'));
                }}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>, fieldType) => {
                  if (throttler) {
                    clearTimeout(throttler);
                  }

                  const value = Number(e.target.value);
                  const minGJ = registrationType === 'moving' ? WIDGET_OPTIONS?.registrationDeskOptions?.minSjvGj : WIDGET_OPTIONS?.registrationDeskOptions?.minSjvM3;
                  const maxGJ = registrationType === 'moving' ? WIDGET_OPTIONS?.registrationDeskOptions?.maxSjvGj : WIDGET_OPTIONS?.registrationDeskOptions?.maxSjvM3;

                  const adjustedValue = Math.max(Math.min(value, maxGJ ?? Number.MAX_VALUE), minGJ ?? Number.MIN_VALUE);

                  setThrottler(setTimeout(() => {
                    if (fieldType === 'RangeField') {
                      gtm.trackUserEvent(String(APP_ID), 'Adjusted the usageSlider', 'Used the slider to adjust the usage');
                    } else if (fieldType === 'NumberField') {
                      gtm.trackUserEvent(String(APP_ID), 'Adjusted the usageSlider', 'Used manual input to adjust the usage');
                    }
                    if (registrationType === 'renovation') {
                      onUpdate({
                        usageM3: adjustedValue.toString(),
                      });
                      setTouched(true);
                      return;
                    }

                    onUpdate({
                      usageM3: String(gjtom3(adjustedValue)),
                    });
                    setTouched(true);
                  }, USAGE_SLIDER_TIMEOUT));
                }}
                visualMinMax
              />
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default AdjustUsageSlider;
